import { Nil } from '../type-utils';
import { AccelerationDisplayUnits, SpeedDisplayUnits } from './convertUnit';
import { isFiniteNumber } from '../numbers';

// the s^2 does not change the ratio
const MS_TO_KMH = 3.6;
const MS_TO_MPH = 2.23694;
const MS_TO_CM_S = 100;
const MS_TO_IN_S = 39.3701;

const KMH_TO_MS = 1 / MS_TO_KMH;
const MPH_TO_MS = 1 / MS_TO_MPH;
const CM_S_TO_MS = 1 / MS_TO_CM_S;
const IN_S_TO_MS = 1 / MS_TO_IN_S;

/**
 * Converts a value from a normalized base unit (m/s or m/s^2)
 * to a display unit (e.g., km/h, mph, cm/s, in/s).
 *
 * This manual conversion is necessary because the chosen utility library, `convert`, does not handle motion-related units.
 *
 * @param value The meter value to be converted. This is the value like it comes from the DB
 * @param to The display unit to we are converting to.
 */
export const convertMeterToDisplayUnit = (
    value: number | Nil,
    to: SpeedDisplayUnits | AccelerationDisplayUnits,
): number | Nil => {
    if (!isFiniteNumber(value)) {
        return value;
    }

    switch (to) {
        case 'km/h':
            return value * MS_TO_KMH;
        case 'mph':
            return value * MS_TO_MPH;
        case 'cm/s':
        case 'cm/s^2':
            return value * MS_TO_CM_S;
        case 'in/s':
        case 'in/s^2':
            return value * MS_TO_IN_S;
        default: {
            // For exhaustive type checking and to handle cases where 'to' is not a supported motion unit

            const _exhaustiveCheck: never = to;
            throw Error(`MotionConverter: got unexpected unit ${_exhaustiveCheck}`);
        }
    }
};

/**
 * Converts a display unit value (e.g., cm/s, in/s, km/h, mph) to a normalized
 * value in meters per second (m/s) or meters per second squared (m/s^2).
 *
 * @param value The value to be converted into meters used by the DB.
 * @param from The display unit to convert from.
 */
export const convertDisplayUnitValueToMeter = (
    value: number | Nil,
    from: SpeedDisplayUnits | AccelerationDisplayUnits,
): number | Nil => {
    if (!isFiniteNumber(value)) {
        return null;
    }

    switch (from) {
        case 'km/h':
            return value * KMH_TO_MS;
        case 'mph':
            return value * MPH_TO_MS;
        case 'cm/s':
        case 'cm/s^2':
            return value * CM_S_TO_MS;
        case 'in/s':
        case 'in/s^2':
            return value * IN_S_TO_MS;
        default: {
            const _exhaustiveCheck: never = from;
            throw Error(`MotionConverter: got unexpected unit ${_exhaustiveCheck}`);
        }
    }
};
